import { METER_TYPE_OPTIONS } from '../schemas/priceSimulationFormSchema';

export const priceSimulationSteps = {
  prospectCheck: 'prospectCheck',
  flowSelector: 'flowSelector',
  customerTypeCheck: 'customerTypeCheck',
  postalCodeCheck: 'postalCodeCheck',
  energyTypeCheck: 'energyTypeCheck',
  currentEnergyConsumptionQuestion: 'currentEnergyConsumptionQuestion',
  installationCheck: 'installationCheck',
  heatingCheck: 'heatingCheck',
  propertyTypeCheck: 'propertyTypeCheck',
  houseHoldSize: 'houseHoldSize',
  meterTypeCheck: 'meterTypeCheck',
  electricityConsumption: 'electricityConsumption',
  peakEnergyConsumption: 'peakEnergyConsumption',
  gasConsumption: 'gasConsumption',
  solarInstallDate: 'solarInstallDate',
  biDirectionalMeterStep: 'biDirectionalMeterStep',
  meterRegistersTypeCheck: 'meterRegistersTypeCheck',
  solarConsumption: 'solarConsumption',
  priceSimulationResult: 'priceSimulationResult',
} as const;

export type PriceSimulationStep = (typeof priceSimulationSteps)[keyof typeof priceSimulationSteps];

// This is used because for most steps, the header
export const activeHeaderIndexForStep = new Map<PriceSimulationStep | null, number>([
  ['prospectCheck', 0],
  ['flowSelector', 0],
  ['customerTypeCheck', 0],
  ['postalCodeCheck', 0],
  ['energyTypeCheck', 0],
  ['currentEnergyConsumptionQuestion', 0],
  ['installationCheck', 0],
  ['heatingCheck', 0],
  ['propertyTypeCheck', 0],
  ['houseHoldSize', 0],
  ['meterTypeCheck', 0],
  ['electricityConsumption', 0],
  ['peakEnergyConsumption', 0],
  ['gasConsumption', 0],
  ['solarInstallDate', 0],
  ['meterRegistersTypeCheck', 0],
  ['solarConsumption', 0],
  ['priceSimulationResult', 1],
]);

export type FlowChildProps = {
  nextMainStep: (step?: PriceSimulationStep) => void;
};

type MeterType = (typeof METER_TYPE_OPTIONS)[keyof typeof METER_TYPE_OPTIONS];

export type StepBuilderParams = {
  isLoggedIn: boolean;
  isProspect: boolean;
  isSoho: boolean;
  isAllowedConsumptionEstimationFlag: boolean;
  isConsumptionKnown: boolean;
  isGas: boolean;
  isElectricity: boolean;
  hasSolarPanels: boolean;
  isRegionWalloon: boolean;
  meterType: MeterType;
};

const addElectricitySteps = (steps: PriceSimulationStep[], params: StepBuilderParams) => {
  const { meterType, isRegionWalloon, hasSolarPanels } = params;

  steps.push('meterTypeCheck');
  if (isRegionWalloon && hasSolarPanels && meterType === METER_TYPE_OPTIONS.DIGITAL) {
    steps.push('solarInstallDate');
  } else if (isRegionWalloon && hasSolarPanels && meterType === METER_TYPE_OPTIONS['BI-DIRECTIONAL']) {
    steps.push('biDirectionalMeterStep');
  }
  steps.push('meterRegistersTypeCheck');

  steps.push('electricityConsumption');
  if (meterType === METER_TYPE_OPTIONS.DIGITAL && !isRegionWalloon) {
    steps.push('peakEnergyConsumption');
  }

  if (hasSolarPanels) {
    steps.push('solarConsumption');
  }
};

const addConsumptionEstimationSteps = (steps: PriceSimulationStep[]) => {
  steps.push('heatingCheck');
  steps.push('propertyTypeCheck');
  steps.push('houseHoldSize');
};

export const buildDynamicStepOrder = (params: StepBuilderParams): PriceSimulationStep[] => {
  const {
    isLoggedIn,
    isProspect,
    isSoho,
    isAllowedConsumptionEstimationFlag,
    isConsumptionKnown,
    isGas,
    isElectricity,
  } = params;
  const steps: PriceSimulationStep[] = [];

  if (!isLoggedIn) {
    steps.push('prospectCheck');
  }
  if (!isProspect) {
    steps.push('flowSelector');
  }
  steps.push('customerTypeCheck');

  steps.push('postalCodeCheck');
  steps.push('energyTypeCheck');

  if (!isSoho && isAllowedConsumptionEstimationFlag) {
    steps.push('currentEnergyConsumptionQuestion');
  }
  steps.push('installationCheck');

  const shouldEstimateConsumption = isAllowedConsumptionEstimationFlag && !isConsumptionKnown && !isSoho;

  if (shouldEstimateConsumption) {
    addConsumptionEstimationSteps(steps);
  } else if (isElectricity) {
    addElectricitySteps(steps, params);
  }

  if (!shouldEstimateConsumption && isGas) {
    steps.push('gasConsumption');
  }

  steps.push('priceSimulationResult');

  return steps;
};
